import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/core/lib/utils';
import { formatTimeAgo } from '@/shared/utils/formatTimeAgo';

interface NotificationCardProps {
  /** Avatar component for the sender — UserAvatar for users, OrgAvatar for orgs */
  avatar: ReactNode;
  /** Primary line: sender name, optionally including notification type */
  title: string;
  /** Secondary line: description of the notification */
  subtitle: string;
  /** Optional third line: action content (e.g. comment text on a task) */
  body?: string;
  /** Organization this item belongs to — shown when the list mixes several (e.g. a global feed). */
  orgLabel?: string;
  /** ISO 8601 date string — rendered as relative time */
  timestamp: string;
  /** Unread state: bold title + primary left border. Defaults to true (read) */
  isRead?: boolean;
  onClick?: () => void;
  testId?: string;
}

/**
 * Generic notification card for the header notifications panel.
 *
 * Agnostic to notification type — callers compose the avatar and supply
 * title/subtitle text via i18n. Unread state adds a bold title and a
 * 3px primary-colored left border.
 *
 * @example
 * <NotificationCard
 *   avatar={<OrgAvatar name="Acme" color="indigo-nebula" size="sm" />}
 *   title="Acme Corp · Invitation"
 *   subtitle="Carlos López invited you to join as member"
 *   timestamp={invitation.createdAt}
 *   isRead={false}
 *   onClick={handleOpen}
 *   testId="invitations-card-inv-1"
 * />
 */
export function NotificationCard({
  avatar,
  title,
  subtitle,
  body,
  orgLabel,
  timestamp,
  isRead = true,
  onClick,
  testId,
}: NotificationCardProps) {
  const { i18n } = useTranslation();
  const timeAgo = formatTimeAgo(timestamp, i18n.language);

  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={cn(
        'w-full flex items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/50',
        'border-l-[3px]',
        isRead ? 'border-l-transparent' : 'border-l-primary',
      )}
    >
      <div className="shrink-0 mt-0.5">{avatar}</div>

      <div className="flex-1 min-w-0 flex flex-col gap-0.5">
        <div className="flex items-start justify-between gap-2">
          <p className={cn('text-sm min-w-0 truncate', !isRead ? 'font-semibold' : 'font-medium')}>
            {title}
          </p>
          <span className="text-[10px] text-muted-foreground/60 shrink-0 mt-px leading-4">
            {timeAgo}
          </span>
        </div>

        <p className="text-xs text-muted-foreground line-clamp-2">{subtitle}</p>

        {body && <p className="text-xs text-muted-foreground/70 italic truncate mt-0.5">{body}</p>}

        {orgLabel && (
          <span className="text-[10px] text-muted-foreground/60 truncate mt-0.5">{orgLabel}</span>
        )}
      </div>
    </button>
  );
}
